import React, { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./LocationMap.css";

const createIcon = () =>
  L.divIcon({
    className: "custom-map-marker",
    html: `<div style="
      width:28px;height:28px;
      background:linear-gradient(135deg,#ff6a00,#ff3c00);
      border:3px solid #fff;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 4px 12px rgba(255,106,0,0.4);
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });

export default function LocationMap({ config, value, onChange }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);

  onChangeRef.current = onChange;

  const placeMarker = useCallback(async (lat, lng) => {
    const map = mapInstance.current;
    if (!map) return;

    let address = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      if (data?.display_name) address = data.display_name;
    } catch {
      /* coordinates fallback */
    }

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { icon: createIcon() }).addTo(map);
    }

    onChangeRef.current({ lat, lng, address });
  }, []);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      center: [config.defaultCenter.lat, config.defaultCenter.lng],
      zoom: config.defaultZoom,
      scrollWheelZoom: true,
    });

    L.tileLayer(config.tileUrl, {
      attribution: config.attribution,
    }).addTo(map);

    map.on("click", (e) => {
      placeMarker(e.latlng.lat, e.latlng.lng);
    });

    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
      markerRef.current = null;
    };
  }, [config, placeMarker]);

  return (
    <div className="location-map-section">
      <div className="location-map-header">
        <span className="location-map-label">{config.label}</span>
        <span className="location-map-hint">{config.hint}</span>
      </div>

      <div className="location-map-wrapper">
        <div ref={mapRef} className="location-map-container" />
      </div>

      {value?.lat && (
        <div className="location-selected-card">
          <span className="location-selected-icon">📍</span>
          <div className="location-selected-info">
            <div className="location-selected-label">{config.selectedLabel}</div>
            <div className="location-selected-address">{value.address}</div>
            <div className="location-selected-coords">
              {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
